import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Flame, Plus, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";

function AreaDialog({ open, onOpenChange, area, onSaved }) {
  const [form, setForm] = useState({ nombre: "", descripcion: "", orden: 0 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        nombre: area?.nombre || "",
        descripcion: area?.descripcion || "",
        orden: area?.orden ?? 0,
      });
    }
  }, [open, area]);
  const sistema = !!area?.sistema;

  const guardar = async () => {
    const nombre = form.nombre.trim();
    if (!nombre || nombre.length < 2) return toast.error("El nombre debe tener al menos 2 caracteres");
    setSaving(true);
    try {
      if (area?.id) {
        await api.patch(`/areas/${area.id}`, {
          nombre: sistema ? undefined : nombre,
          descripcion: form.descripcion || null,
          orden: Number(form.orden) || 0,
        });
        toast.success(`Área "${nombre}" actualizada correctamente.`);
      } else {
        await api.post("/areas", { nombre, descripcion: form.descripcion || null, orden: Number(form.orden) || 0 });
        toast.success(`Área "${nombre}" creada correctamente.`);
      }
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{area?.id ? "Editar área" : "Nueva área"}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {sistema && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
              <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                Esta es un área del sistema (necesaria para Cocina/Bebidas/Freidora).
                Su nombre no se puede modificar para no romper los monitores. Puedes
                ajustar su orden o desactivarla.
              </div>
            </div>
          )}
          <div>
            <Label>Nombre</Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Cocina, Bebidas, Parrilla, Barra…"
              maxLength={40}
              disabled={sistema}
              data-testid="area-nombre-input"
            />
          </div>
          <div>
            <Label>Descripción (opcional)</Label>
            <Input
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Ej. Cocina caliente"
              data-testid="area-descripcion-input"
            />
          </div>
          <div>
            <Label>Orden</Label>
            <Input
              type="number"
              min="0"
              value={form.orden}
              onChange={(e) => setForm({ ...form, orden: e.target.value })}
              data-testid="area-orden-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="area-guardar-btn">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AreasAdmin() {
  const { selectedSucursalId } = useAuth();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/areas", { params: { only_active: false } });
      setAreas(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedSucursalId]);

  // Refrescar al cambiar de sucursal
  useEffect(() => {
    const h = () => load();
    window.addEventListener("sucursal-changed", h);
    return () => window.removeEventListener("sucursal-changed", h);
  }, []);

  const toggleActive = async (a) => {
    try {
      await api.patch(`/areas/${a.id}`, { active: !a.active });
      toast.success(`Área "${a.nombre}" ${a.active ? "desactivada" : "activada"} correctamente.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Áreas de preparación</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configura las áreas operativas de esta sucursal. Cada producto se envía a un área (Cocina, Bebidas, Freidora, Parrilla…).
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-area-btn">
          <Plus className="h-4 w-4 mr-1" />Nueva área
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Cargando…</div>
      ) : areas.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          No hay áreas configuradas. Crea la primera con "Nueva área".
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a) => (
            <Card key={a.id} className={a.active ? "" : "opacity-60"} data-testid={`area-card-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Flame className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-black text-lg truncate" title={a.nombre}>{a.nombre}</div>
                      {a.descripcion && <div className="text-xs text-muted-foreground truncate">{a.descripcion}</div>}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {a.sistema && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 font-bold inline-flex items-center gap-0.5">
                            <Lock className="h-2.5 w-2.5" />Sistema
                          </span>
                        )}
                        {!a.active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-bold">Inactiva</span>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`edit-area-${a.id}`}>
                    <Pencil className="h-3 w-3 mr-1" />Editar
                  </Button>
                  <Button size="sm" variant={a.active ? "ghost" : "default"} onClick={() => toggleActive(a)} data-testid={`toggle-area-${a.id}`}>
                    {a.active ? "Desactivar" : "Activar"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AreaDialog open={dialogOpen} onOpenChange={setDialogOpen} area={editing} onSaved={load} />
    </div>
  );
}
